


























import gql from 'graphql-tag';
import Vue from 'vue';
import { get, has, kebabCase, isInteger, keyBy, toNumber, includes } from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import { canUserRoleEdit } from '@/utils/UIAccessUtils';
import SorListing from '@/components/listing/SorListing.vue';
import UrlParamHandler from '@/components/listing/components/user_preferences/UrlParamHandler';
import { isOldUrlSyntax, translateOldUrlSyntax } from './urlTranslation';
import InvalidState from './InvalidState.vue';

// key is in kebab case form of objectType.namePlural format, i.e.: 'programs' or 'data-assets'
interface ObjectTypeMeta {
  [key: string]: string;
}

const DEFAULT_PAGE = 1;

export default Vue.extend({
  name: 'SorListingPage',
  components: {
    InvalidState,
    SorListing,
  },
  mixins: [UrlParamHandler],
  props: {
    objectTypeRaw: {
      /**
       * Input to component comes from route param, which should be in kebab case form
       * of objectType.namePlural format, i.e.: 'programs' or 'data-assets'
       */
      type: String,
      required: true,
    },
  },
  data() {
    return {
      objectTypeMeta: {} as ObjectTypeMeta,
      page: DEFAULT_PAGE,
      userRole: null,
    };
  },
  computed: {
    ...mapGetters(['featureFlags']),
    skippedObjectTypes(): [] {
      let skippedObjectTypes = [];
      if (!this.featureFlags.new_listing_event_logs) {
        skippedObjectTypes.push('Revision');
      }
      if (!this.featureFlags.new_listing_tags) {
        skippedObjectTypes.push('TagInfo');
      }
      if (!this.featureFlags.new_listing_survey_responses) {
        skippedObjectTypes.push('SurveyResponse');
      }
      return skippedObjectTypes;
    },
    shouldUseOldSoRListing(): boolean {
      return includes(this.skippedObjectTypes, this.objectTypeRaw);
    },
    enableEditing() {
      if (!this.featureFlags || !this.userRole) return false;
      return canUserRoleEdit(this.userRole, this.objectType, this.featureFlags.restricted_contributor);
    },
    oldFavorite() {
      const oldFavorite = this.$route.query['bookmark'];
      return oldFavorite ? JSON.parse(oldFavorite) : null;
    },
    loading(): boolean {
      return this.$apollo.loading;
    },
    invalid(): boolean {
      let newSyntax: object | null = null;

      // Don't show "Invalid object type" on new listing page
      if (this.shouldUseOldSoRListing) {
        return { invalid: false, newSyntax: null };
      }

      if (isOldUrlSyntax(this.objectTypeRaw, this.objectTypeMetaOldSyntax)) {
        newSyntax = translateOldUrlSyntax(this.$route, this.objectTypeMetaOldSyntax, this.attributeMeta);
      }

      return { invalid: !has(this.objectTypeMeta, this.objectTypeRaw), newSyntax };
    },
    objectType(): string | undefined {
      let objectType = isOldUrlSyntax(this.objectTypeRaw, this.objectTypeMetaOldSyntax)
        ? this.objectTypeRaw
        : get(this.objectTypeMeta, [this.objectTypeRaw, 'objectType']);
      if (objectType == 'events-log') objectType = 'Revision';

      return objectType;
    },
  },
  watch: {
    $route: {
      handler: function(to: Route): void {
        if (this.$apollo.queries.objectTypeMetaOldSyntax.loading) return false;

        // Leave Vue router and use old listing page
        if (this.shouldRouteToOldSoRListing(to.path)) {
          window.location.href = to.fullPath;
        } else {
          let pageValue: string | undefined = to.query['page'];
          const page = this.toPage(pageValue);

          if (!page) {
            this.pageChange(DEFAULT_PAGE);
            return;
          }
          this.page = page;
        }
      },
    },

    objectTypeRaw: {
      handler() {
        this.resetUserPreferences();
      },
    },
    invalid: {
      deep: true,
      handler(invalid: { invalid: boolean; newSyntax: object }) {
        const title: string = get(this.objectTypeMeta, [this.objectTypeRaw, 'namePlural']);
        const pageTitleConfig = invalid.invalid ? { title: 'System of Record' } : { title, visible: false };

        if (invalid.newSyntax && !this.shouldUseOldSoRListing) {
          const encodedPrefs = this.encodeUserPreferences({
            sor: {
              pageSize: invalid.newSyntax.pageSize,
              columns: invalid.newSyntax.columns,
              sort: invalid.newSyntax.sort,
              filters: invalid.newSyntax.filters,
            },
          });

          const path: string =
            this.$route.name === 'sorListingPageBeta'
              ? `/beta/listing/${invalid.newSyntax.objectType}`
              : `/sor/listing/${invalid.newSyntax.objectType}`;
          const page: number | undefined = invalid.newSyntax.page;
          const bookmark: string | undefined = invalid.newSyntax.bookmark;
          const query = {
            page,
            bookmark,
            p: encodedPrefs,
          };

          this.$router.push({
            path,
            query,
          });
          // Just reload the whole page to avoid a race condition
          window.location.href = this.$route.fullPath;

          return;
        }
        this.setPageTitle(pageTitleConfig);
      },
    },
  },
  methods: {
    ...mapActions(['setPageTitle']),
    shouldRouteToOldSoRListing(route: string): boolean {
      const skippedRoutes = this.skippedObjectTypes.map(function(objectType) {
        return '/sor/listing/' + objectType;
      });
      return skippedRoutes.some(r => route === r);
    },
    toPage(pageValue: string | undefined): string | undefined {
      const positiveInt = val => val > 0;
      const validPageValue = pageValue => {
        const page = toNumber(pageValue);
        return isInteger(page) && positiveInt(page);
      };

      if (!validPageValue(pageValue)) return;

      return toNumber(pageValue);
    },
    pageChange(page: number): void {
      this.$router.replace({
        query: {
          ...this.$route.query,
          page,
        },
      });
    },
    // We do not yet support refreshing only the LHN, so a full
    // page refresh is required to refresh LHN entries
    oldFavoriteChange(data): void {
      if (data.state === 'SAVED') {
        window.location.href = data.data.redirectUrl;
      }
      if (data.state === 'DELETED') {
        this.$router.replace({
          query: {
            ...this.$route.query,
            bookmark: undefined,
          },
        });
        window.location.href = this.$route.fullPath;
      }
    },
  },
  apollo: {
    userRole: {
      query: gql`
        query GetCurrentUserRole {
          currentUser {
            userRole {
              value
            }
          }
        }
      `,
      update(result) {
        return get(result, 'currentUser.userRole.value', '');
      },
    },
    objectTypeMeta: {
      query: gql`
        query ObjectTypeMeta {
          objectTypes {
            objectType
            namePlural
          }
        }
      `,
      update: data => keyBy(data.objectTypes, meta => kebabCase(meta.namePlural)),
    },
    // TODO: combine with above, and use computed to map keys
    objectTypeMetaOldSyntax: {
      query: gql`
        query ObjectTypeMeta {
          objectTypes {
            objectType
            namePlural
          }
        }
      `,
      update: data => keyBy(data.objectTypes, meta => meta.objectType),
    },
    attributeMeta: {
      query: gql`
        query Attributes($objectType: SearchType!, $context: AttributesContext!) {
          objectType(name: $objectType) {
            attrs(context: $context) {
              name
              backendKey
            }
          }
        }
      `,
      variables() {
        return {
          objectType: this.objectType,
          context: 'SOR_LISTING',
        };
      },
      skip() {
        return !this.objectType;
      },
      update: data => data.objectType.attrs,
    },
  },
});
